import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Outlet, useOutlet, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import { IntegrationSdkDocs } from './IntegrationSdkDocs';
function AddProjectModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({ application_name: '', application_description: '', redirect_uris: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successData, setSuccessData] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setFormData({ application_name: '', application_description: '', redirect_uris: '' });
    setSuccessData(null);
    setErrors({});
    setHasDownloaded(false);
    onClose();
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDownload = () => {
    if (!successData) return;
    const creds = {
      application_name: successData.application_name,
      client_id: successData.client_id,
      client_secret: successData.plain_secret,
      redirect_uris: successData.redirect_uris,
      created_at: successData.created_at
    };
    const blob = new Blob([JSON.stringify(creds, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${successData.application_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-credentials.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setHasDownloaded(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const uris = formData.redirect_uris.split(',').map(u => u.trim()).filter(Boolean);
      const response = await api.post('/users/me/projects', {
        application_name: formData.application_name,
        application_description: formData.application_description,
        redirect_uris: uris,
      });
      setSuccessData(response.data);
      onSuccess();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
      } else {
        setErrors({ general: ['An unexpected error occurred. Please try again.'] });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className={`bg-white dark:bg-slate-900 w-full ${successData ? 'max-w-2xl' : 'max-w-md'} rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden transition-all`}>
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {successData ? 'Application Registered Successfully' : 'Add New System'}
          </h3>
          {!successData && (
            <button onClick={handleClose} className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 focus:outline-none">
               ✕
            </button>
          )}
        </div>
        
        {successData ? (
          <div className="p-6 text-left">
            <div className="mb-6 p-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 flex items-start gap-3 text-sm">
              <span className="text-xl">⚠️</span>
              <div>
                <strong>IMPORTANT:</strong> Your Client Secret is shown only once. For security reasons, we store it in a hashed format and it cannot be retrieved in plain text again. Please copy these confidential credentials or download them immediately and store them in a secure location. You will be notified via in-app notification, email, and SMS once your application has been reviewed and approved.
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Client ID</label>
                <div className="flex items-center gap-2">
                  <input type="text" readOnly value={successData.client_id} className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-mono text-sm outline-none" />
                  <button onClick={() => handleCopy(successData.client_id, 'client_id')} className="p-2 rounded-md border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400">
                    {copiedField === 'client_id' ? '✅' : '📋'}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Client Secret</label>
                <div className="flex items-center gap-2">
                  <input type="text" readOnly value={successData.plain_secret} className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-mono text-sm outline-none" />
                  <button onClick={() => handleCopy(successData.plain_secret, 'client_secret')} className="p-2 rounded-md border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400">
                    {copiedField === 'client_secret' ? '✅' : '📋'}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-800 pt-4">
              <button 
                onClick={handleDownload}
                className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                {hasDownloaded ? 'Download' : 'Download Credentials'}
              </button>
              {hasDownloaded && (
                <button 
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 text-left">
            {errors.general && (
               <div className="p-3 bg-red-50 text-red-700 text-sm rounded dark:bg-red-900/30 dark:text-red-400">
                 {errors.general[0]}
               </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">System Name <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                required 
                value={formData.application_name}
                onChange={e => setFormData({...formData, application_name: e.target.value})}
                className={`w-full px-3 py-2 rounded-md border bg-transparent text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none ${errors.application_name ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'}`}
                placeholder="My awesome integration"
              />
              {errors.application_name && <p className="mt-1 text-xs text-red-500">{errors.application_name[0]}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
              <textarea 
                value={formData.application_description}
                onChange={e => setFormData({...formData, application_description: e.target.value})}
                className={`w-full px-3 py-2 rounded-md border bg-transparent text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none ${errors.application_description ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'}`}
                placeholder="Brief description of the system..."
                rows={3}
              />
              {errors.application_description && <p className="mt-1 text-xs text-red-500">{errors.application_description[0]}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Redirect URIs <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                required 
                value={formData.redirect_uris}
                onChange={e => setFormData({...formData, redirect_uris: e.target.value})}
                className={`w-full px-3 py-2 rounded-md border bg-transparent text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none ${errors.redirect_uris || Object.keys(errors).some(k => k.startsWith('redirect_uris.')) ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'}`}
                placeholder="https://app.com/callback, https://test.com/cb"
              />
              <p className="mt-1 text-xs text-slate-500">Comma-separated list of valid URLs</p>
              {errors.redirect_uris && <p className="mt-1 text-xs text-red-500">{errors.redirect_uris[0]}</p>}
              {Object.keys(errors).some(k => k.startsWith('redirect_uris.')) && (
                 <p className="mt-1 text-xs text-red-500">One or more URIs are invalid.</p>
              )}
            </div>
            
            <div className="pt-4 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
              <button 
                type="button" 
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading && <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                Add System
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}



function DeleteConfirmModal({ isOpen, onClose, onConfirm, loading }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 p-6 text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Delete Project</h3>
        <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">Are you sure you want to delete this project? This action cannot be undone and will permanently break any active integrations.</p>
        <div className="flex justify-center gap-3">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm font-medium">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading && <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function EditProjectModal({ isOpen, onClose, onSuccess, project }) {
  const [formData, setFormData] = useState({ application_name: '', application_description: '', redirect_uris: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (project) {
      setFormData({
        application_name: project.name || project.application_name || '',
        application_description: project.description || project.application_description || '',
        redirect_uris: Array.isArray(project.redirectUris || project.redirect_uris) ? (project.redirectUris || project.redirect_uris).join(', ') : '',
      });
      setErrors({});
    }
  }, [project, isOpen]);

  if (!isOpen || !project) return null;

  const isSuspended = project.status === 'Suspended' || project.status === 'Rejected';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSuspended) return;
    
    setErrors({});
    setLoading(true);

    try {
      const uris = formData.redirect_uris.split(',').map(u => u.trim()).filter(Boolean);
      await api.put(`/users/me/projects/${project.id}`, {
        application_name: formData.application_name,
        application_description: formData.application_description,
        redirect_uris: uris,
      });
      onSuccess();
      onClose();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
      } else {
        setErrors({ general: [err.response?.data?.message || 'An unexpected error occurred. Please try again.'] });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Edit System</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 focus:outline-none">✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-left">
          {isSuspended ? (
             <div className="p-3 bg-red-50 text-red-800 text-sm rounded dark:bg-red-900/30 dark:text-red-200 flex gap-2">
               <span>🚫</span>
               <span>Editing is disabled because this app has been Suspended/Rejected by an administrator.</span>
             </div>
          ) : (
            <div className="p-3 bg-amber-50 text-amber-800 text-sm rounded dark:bg-amber-900/30 dark:text-amber-200 flex gap-2">
              <span>⚠️</span>
              <span>Editing will require admin re-approval and pause the current system connection until approved.</span>
            </div>
          )}
          
          {errors.general && (
             <div className="p-3 bg-red-50 text-red-700 text-sm rounded dark:bg-red-900/30 dark:text-red-400">
               {errors.general[0]}
             </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">System Name *</label>
            <input type="text" required disabled={isSuspended} value={formData.application_name} onChange={e => setFormData({...formData, application_name: e.target.value})} className={`w-full px-3 py-2 rounded-md border bg-transparent text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none ${errors.application_name ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'} ${isSuspended ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800' : ''}`} />
            {errors.application_name && <p className="mt-1 text-xs text-red-500">{errors.application_name[0]}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <textarea disabled={isSuspended} value={formData.application_description} onChange={e => setFormData({...formData, application_description: e.target.value})} className={`w-full px-3 py-2 rounded-md border bg-transparent text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none ${errors.application_description ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'} ${isSuspended ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800' : ''}`} rows={3} />
            {errors.application_description && <p className="mt-1 text-xs text-red-500">{errors.application_description[0]}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Redirect URIs *</label>
            <input type="text" required disabled={isSuspended} value={formData.redirect_uris} onChange={e => setFormData({...formData, redirect_uris: e.target.value})} className={`w-full px-3 py-2 rounded-md border bg-transparent text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none ${errors.redirect_uris ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'} ${isSuspended ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800' : ''}`} />
            <p className="mt-1 text-xs text-slate-500">Comma-separated URLs</p>
            {errors.redirect_uris && <p className="mt-1 text-xs text-red-500">{errors.redirect_uris[0]}</p>}
          </div>
          
          <div className="pt-4 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">{isSuspended ? 'Close' : 'Cancel'}</button>
            <button type="submit" disabled={loading || isSuspended} className="px-4 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 focus:ring-2 focus:ring-amber-500 disabled:opacity-50 flex items-center gap-2">
              {loading && <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
              Confirm & Edit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RevertConfirmModal({ isOpen, onClose, onConfirm, loading }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 p-6 text-center">
        <div className="text-4xl mb-4">⏪</div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Revert Changes</h3>
        <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">Are you sure you want to discard your pending edits and revert to the last approved state? This will instantly restore your active connection.</p>
        <div className="flex justify-center gap-3">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm font-medium">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {loading && <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
            Revert
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppWorkspace(props) {
  const navigate = useNavigate();
  const params = useParams();
  const accountIndex = params?.accountIndex ?? '0';
  const consoleBasePath = `/u/${accountIndex}/dashboard/console`;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/users/me/projects/${selectedProject.id}`);
      setDeleteModalOpen(false);
      triggerReload();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevert = async () => {
    setActionLoading(true);
    try {
      await api.post(`/users/me/projects/${selectedProject.id}/revert`);
      setRevertModalOpen(false);
      triggerReload();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };




  let context = {};
  try {
    context = useOutletContext() || {};
  } catch {
    context = {};
  }

  const sortedProjects = props.sortedProjects || context.sortedProjects || [];
  const fetchParams = props.fetchParams || context.fetchParams || { page: 1, sort_by: 'created_at', sort_dir: 'desc', status: 'all' };
  const setFetchParams = props.setFetchParams || context.setFetchParams || (() => {});
  const paginationMeta = props.paginationMeta || context.paginationMeta || null;
  const triggerReload = props.triggerReload || context.triggerReload || (() => {});
  const isLoading = props.isLoading ?? context.isLoading ?? false;

  const hasPending = props.hasPending !== undefined
    ? props.hasPending
    : (context.hasPending ?? sortedProjects.some((p) => p.status === 'PENDING_APPROVAL'));

  const handleSort = (field) => {
    setFetchParams(prev => ({
      ...prev,
      sort_by: field,
      sort_dir: prev.sort_by === field && prev.sort_dir === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const handleFilter = (status) => {
    setFetchParams(prev => ({ ...prev, status, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFetchParams(prev => ({ ...prev, page: newPage }));
  };

  return (
    <>
      {hasPending && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 rounded">
          You have an external system awaiting administrative review. Please wait for approval before adding another project.
        </div>
      )}

      {/* Filters and Controls */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'approved', 'suspended'].map(s => (
            <button
              key={s}
              onClick={() => handleFilter(s)}
              className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                fetchParams.status === s 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={triggerReload}
          disabled={isLoading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 dark:focus:ring-offset-slate-900"
        >
          <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reload
        </button>
      </div>

      <div className="overflow-x-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
        <table className="min-w-full text-left">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr className="text-sm text-slate-500 dark:text-slate-400">
              <th className="px-4 py-3 font-medium min-w-[14rem] cursor-pointer hover:text-slate-700 dark:hover:text-slate-200" onClick={() => handleSort('application_name')}>
                Project Title {fetchParams.sort_by === 'application_name' && (fetchParams.sort_dir === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 font-medium min-w-[10rem]">Client ID</th>
              <th className="px-4 py-3 font-medium min-w-[8rem]">Status</th>
              <th className="px-4 py-3 font-medium min-w-[8rem] cursor-pointer hover:text-slate-700 dark:hover:text-slate-200" onClick={() => handleSort('created_at')}>
                Created {fetchParams.sort_by === 'created_at' && (fetchParams.sort_dir === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 font-medium min-w-[12rem]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {sortedProjects.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                  No projects found.
                </td>
              </tr>
            ) : (
              sortedProjects.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-white">{p.name}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 font-mono">{p.clientId}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      p.status === 'Live' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                      : p.status === 'Suspended' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => navigate(`${consoleBasePath}/workspace/${p.id}`)}
                        title="View Details"
                        className="p-1 rounded-md text-slate-500 hover:bg-slate-100 hover:text-indigo-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProject(p);
                          setEditModalOpen(true);
                        }}
                        title="Edit Application"
                        className="p-1 rounded-md text-slate-500 hover:bg-slate-100 hover:text-amber-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-amber-400 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      {p.last_approved_state && p.status === 'PENDING_APPROVAL' && (
                        <button
                          onClick={() => {
                            setSelectedProject(p);
                            setRevertModalOpen(true);
                          }}
                          title="Revert to Approved State"
                          className="p-1 rounded-md text-slate-500 hover:bg-slate-100 hover:text-indigo-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedProject(p);
                          setDeleteModalOpen(true);
                        }}
                        title="Delete Application"
                        className="p-1 rounded-md text-slate-500 hover:bg-slate-100 hover:text-red-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-red-400 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {paginationMeta && paginationMeta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-between border-t border-slate-200 dark:border-slate-800 pt-4">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Showing page <span className="font-medium text-slate-900 dark:text-white">{paginationMeta.current_page}</span> of <span className="font-medium text-slate-900 dark:text-white">{paginationMeta.last_page}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(paginationMeta.current_page - 1)}
              disabled={paginationMeta.current_page === 1}
              className="px-3 py-1 text-sm font-medium rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(paginationMeta.current_page + 1)}
              disabled={paginationMeta.current_page === paginationMeta.last_page}
              className="px-3 py-1 text-sm font-medium rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        loading={actionLoading}
      />
      <RevertConfirmModal
        isOpen={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        onConfirm={handleRevert}
        loading={actionLoading}
      />
      <EditProjectModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={triggerReload}
        project={selectedProject}
      />
    </>
  );
}

export default function DevConsole() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();

  const accountIndex = params?.accountIndex ?? '0';
  const consoleBasePath = `/u/${accountIndex}/dashboard/console`;

  // Detect active tab from URL path
  const isDocs = location.pathname.endsWith('/docs') || location.pathname.includes('/dashboard/console/docs');
  const activeSubTab = isDocs ? 'docs' : 'workspace';

  const { user, loading: authLoading } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [fetchParams, setFetchParams] = useState({
    page: 1,
    per_page: 10,
    sort_by: 'created_at',
    sort_dir: 'desc',
    status: 'all',
  });
  const [paginationMeta, setPaginationMeta] = useState(null);

  useEffect(() => {
    if (isDocs) {
      setIsLoading(false);
      setError(null);
      return;
    }
    if (authLoading || !user) return; // Strict Request Sequencing: wait for auth context

    const controller = new AbortController();
    let isMounted = true;
    
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.get('/users/me/projects', { 
          params: fetchParams,
          signal: controller.signal
        });
        if (!isMounted) return;
        
        // Map backend OAuthApplication structure to what UI expects
        const mapped = response.data.data.map(app => {
          let status = 'Live';
          if (!app.is_active || app.suspended_at) {
            status = 'Suspended';
          } else if (!app.approved_at) {
            status = 'PENDING_APPROVAL';
          }
          return {
            id: app.id,
            name: app.application_name || 'Unnamed Project',
            clientId: app.client_id,
            status,
            last_approved_state: app.last_approved_state,
            description: app.application_description,
            redirectUris: app.redirect_uris,
            createdAt: new Date(app.created_at).getTime()
          };
        });
        setProjects(mapped);
        setPaginationMeta({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total,
        });
      } catch (err) {
        if (!isMounted) return;
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return; // Ignore canceled requests during navigation/re-renders
        }
        console.error('Failed to fetch projects', err);
        setError('Failed to load projects. Please try again.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchProjects();
    
    return () => { 
      isMounted = false;
      controller.abort();
    };
  }, [authLoading, user, fetchParams, reloadKey, isDocs]);

  // Use backend sorting directly
  const sortedProjects = projects;

  const hasPending = sortedProjects.some((p) => p.status === 'PENDING_APPROVAL');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const createProject = () => {
    if (hasPending) return;
    setIsAddModalOpen(true);
  };

  const triggerReload = () => setReloadKey(k => k + 1);

  return (
    <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg p-6">
      <AddProjectModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
        onSuccess={() => {
           // Toast could go here
           triggerReload();
        }} 
      />
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Developer Console</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Account #{accountIndex} — manage your client integrations</p>
        </div>

        {activeSubTab === 'workspace' && (
          <div>
            <button
              onClick={createProject}
              disabled={hasPending}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${hasPending ? 'bg-slate-600 text-slate-300 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              + Create New Project
            </button>
          </div>
        )}
      </div>

      {/* Sub-Navigation Tab Controller */}
      <div className="mb-6 flex items-center gap-1 bg-slate-100 dark:bg-slate-800/50 p-1 rounded-lg">
        <button
          onClick={() => navigate(`${consoleBasePath}/workspace`)}
          className={`flex-1 px-4 py-2 rounded-md font-medium text-sm transition-colors ${
            activeSubTab === 'workspace'
              ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          🎛️ App Workspace
        </button>
        <button
          onClick={() => navigate(`${consoleBasePath}/docs`)}
          className={`flex-1 px-4 py-2 rounded-md font-medium text-sm transition-colors ${
            activeSubTab === 'docs'
              ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          📚 Integration SDK Docs
        </button>
      </div>

      {/* Content Area */}
      {activeSubTab === 'docs' ? (
        outlet || <IntegrationSdkDocs />
      ) : (isLoading && !paginationMeta) ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-slate-500 dark:text-slate-400">Loading projects...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-lg text-center">
          <p>{error}</p>
        </div>
      ) : outlet ? (
        <Outlet context={{ projects, setProjects, sortedProjects, hasPending, createProject, fetchParams, setFetchParams, paginationMeta, triggerReload, isLoading }} />
      ) : (
        <AppWorkspace
          projects={projects}
          sortedProjects={sortedProjects}
          hasPending={hasPending}
          fetchParams={fetchParams}
          setFetchParams={setFetchParams}
          paginationMeta={paginationMeta}
          triggerReload={triggerReload}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
